import { HttpMethodsEnum } from "../constants/enums";
import type {
  ContactModel,
  DiagnosisModel,
  DiagnosisSearchResponseModel,
  HospitalTenant,
  PaginatedResult,
  PlanModel,
  PresignedUrlModel,
  UserModel,
  NotificationModel,
} from "../types/models";
import type {
  CreatePatientByStaffParams,
  CreatePlanParams,
  GetPatientsParams,
  GetStaffParams,
  ListParams,
  LoginParams,
  RequestOtpLoginParams,
  RequestOtpLoginResponse,
  SearchDiagnosisParams,
  SignupParams,
  UpdatePlanParams,
  UpdateUserStatsParams,
  VerifyOtpLoginParams,
} from "../types/params";
import { persistAuthSession } from "./authStorage";
import { BACKEND_URL } from "./config";
import { makeRequest } from "./http";

export const logInService = async ({ email, password }: LoginParams) => {
  const token = await makeRequest({
    url: `${BACKEND_URL}/user/login`,
    method: HttpMethodsEnum.POST,
    data: { email, password },
  });
  persistAuthSession(token.access_token);
};

export const requestOtpLoginService = async ({
  phone,
}: RequestOtpLoginParams) => {
  return (await makeRequest({
    url: `${BACKEND_URL}/user/login/otp/request`,
    method: HttpMethodsEnum.POST,
    data: { phone },
  })) as RequestOtpLoginResponse;
};

export const verifyOtpLoginService = async ({
  phone,
  otp_code,
}: VerifyOtpLoginParams) => {
  const token = await makeRequest({
    url: `${BACKEND_URL}/user/login/otp/verify`,
    method: HttpMethodsEnum.POST,
    data: { phone, otp_code },
  });
  persistAuthSession(token.access_token);
};

export const signUpService = async (body: SignupParams) => {
  const token = await makeRequest({
    url: `${BACKEND_URL}/user/register`,
    method: HttpMethodsEnum.POST,
    data: body,
  });
  persistAuthSession(token.access_token);
};

export const getHospitalTenantsService = async () => {
  return (await makeRequest({
    url: `${BACKEND_URL}/hospital/tenants`,
    method: HttpMethodsEnum.GET,
  })) as HospitalTenant[];
};

export const forgotPasswordService = async (email: string) => {
  return await makeRequest({
    url: `${BACKEND_URL}/user/forgot_password`,
    method: HttpMethodsEnum.POST,
    data: { email },
  });
};

export const resetPasswordService = async (token: string, password: string) => {
  return await makeRequest({
    url: `${BACKEND_URL}/user/reset_password`,
    method: HttpMethodsEnum.POST,
    data: { token, new_password: password },
  });
};

export const getCurrentUserService = async () => {
  return (await makeRequest({
    url: `${BACKEND_URL}/user`,
    method: HttpMethodsEnum.GET,
  })) as UserModel;
};

export const updateCurrentUserService = async (data: Record<string, unknown>) => {
  return await makeRequest({
    url: `${BACKEND_URL}/user`,
    method: HttpMethodsEnum.PUT,
    data,
  });
};

export const getUserByIdService = async (user_id: string) => {
  return (await makeRequest({
    url: `${BACKEND_URL}/user/${user_id}`,
    method: HttpMethodsEnum.GET,
  })) as UserModel;
};

export const deleteUserService = async (user_id: string) => {
  return await makeRequest({
    url: `${BACKEND_URL}/user/${user_id}`,
    method: HttpMethodsEnum.DELETE,
  });
};

export const getPatientsService = async (params: GetPatientsParams) => {
  return (await makeRequest({
    url: `${BACKEND_URL}/user/patients`,
    method: HttpMethodsEnum.GET,
    params,
  })) as PaginatedResult<UserModel>;
};

export const getStaffService = async (params: GetStaffParams) => {
  return (await makeRequest({
    url: `${BACKEND_URL}/user/staff`,
    method: HttpMethodsEnum.GET,
    params,
  })) as PaginatedResult<UserModel>;
};

export const getContactsService = async () => {
  return (await makeRequest({
    url: `${BACKEND_URL}/user/contacts`,
    method: HttpMethodsEnum.GET,
  })) as ContactModel[];
};

export const createPatientByStaffService = async (
  body: CreatePatientByStaffParams
) => {
  return await makeRequest({
    url: `${BACKEND_URL}/user/patients`,
    method: HttpMethodsEnum.POST,
    data: body,
  });
};

export const getMyHospitalTenantsService = async () => {
  return (await makeRequest({
    url: `${BACKEND_URL}/hospital/my-tenants`,
    method: HttpMethodsEnum.GET,
  })) as HospitalTenant[];
};

export const updatePatientStatsService = async (
  user_id: string,
  body: UpdateUserStatsParams
) => {
  return await makeRequest({
    url: `${BACKEND_URL}/user/${user_id}/stats`,
    method: HttpMethodsEnum.PUT,
    data: body,
  });
};

export const updatePatientMrnService = async (
  user_id: string,
  external_mrn: string
) => {
  return await makeRequest({
    url: `${BACKEND_URL}/user/${user_id}/mrn`,
    method: HttpMethodsEnum.PUT,
    data: { external_mrn },
  });
};

export const getPatientStatsHistoryService = async (user_id: string) => {
  return await makeRequest({
    url: `${BACKEND_URL}/user/${user_id}/stats/history`,
    method: HttpMethodsEnum.GET,
  });
};

export const getPatientDiagnosesService = async (patient_id: string) => {
  return (await makeRequest({
    url: `${BACKEND_URL}/icd/patients/${patient_id}/diagnoses`,
    method: HttpMethodsEnum.GET,
  })) as DiagnosisModel[];
};

export const searchDiagnosesService = async ({
  q,
  limit = 20,
  offset = 0,
}: SearchDiagnosisParams) => {
  return (await makeRequest({
    url: `${BACKEND_URL}/icd/search`,
    method: HttpMethodsEnum.GET,
    params: { q, limit, offset },
  })) as DiagnosisSearchResponseModel;
};

export const createPlanService = async (
  body: CreatePlanParams,
  signal?: AbortSignal
) => {
  return await makeRequest({
    url: `${BACKEND_URL}/plan`,
    method: HttpMethodsEnum.POST,
    data: body,
    signal,
  });
};

export const updatePlanService = async (
  plan_id: string,
  body: UpdatePlanParams,
  signal?: AbortSignal
) => {
  return await makeRequest({
    url: `${BACKEND_URL}/plan/${plan_id}`,
    method: HttpMethodsEnum.PUT,
    data: body,
    signal,
  });
};

export const getPlanByIdService = async (plan_id: string) => {
  return (await makeRequest({
    url: `${BACKEND_URL}/plan/${plan_id}`,
    method: HttpMethodsEnum.GET,
  })) as PlanModel;
};

export const activatePlanByIdService = async (plan_id: string) => {
  return await makeRequest({
    url: `${BACKEND_URL}/plan/${plan_id}`,
    method: HttpMethodsEnum.POST,
  });
};

export const rejectPlanByIdService = async (plan_id: string) => {
  return await makeRequest({
    url: `${BACKEND_URL}/plan/${plan_id}`,
    method: HttpMethodsEnum.DELETE,
  });
};

export const getNotificationsService = async ({ limit, offset }: ListParams) => {
  return (await makeRequest({
    url: `${BACKEND_URL}/notifications`,
    method: HttpMethodsEnum.GET,
    params: { limit, offset },
  })) as PaginatedResult<NotificationModel>;
};

export const uploadFileService = async (file: File, file_type: "custom" | "profile" = "custom") => {
  const mimeType = file.type || "application/octet-stream";
  const file_extension = mimeType.split("/").pop() || "bin";

  const presigned_url = (await makeRequest({
    url: `${BACKEND_URL}/upload`,
    method: HttpMethodsEnum.POST,
    data: { original_name: file.name },
    params: { file_type, file_extension },
  })) as PresignedUrlModel;

  await fetch(presigned_url.upload_url, {
    method: "PUT",
    headers: { "Content-Type": mimeType },
    body: file,
  });

  return [presigned_url];
};

export const uploadAndSetProfileImageService = async (file: File) => {
  const uploaded = await uploadFileService(file, "profile");
  const profile = uploaded[0];
  if (!profile?.file_path) {
    throw new Error("Profile image upload failed.");
  }
  await updateCurrentUserService({ profile_image: profile.file_path });
  return profile.file_path;
};

export const clearProfileImageService = async () => {
  return await updateCurrentUserService({ profile_image: null });
};
